import { priceZodSchema } from "@@/shared/prices/price";
import { asArray } from "@@/utilities/as-array";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type {
    _OrderEventBaseFactoryParams} from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

const orderPayLaterOnPremiseCancelledZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("ON_PREMISE_PAY_LATER_CANCELLED"),
        paymentProvider: z.literal("ON_PREMISE"),
        price: priceZodSchema,
        /** Required on newly created events, but can be missing on old ones */
        cancels: orderEventIdZodSchema.optional(),
        paymentFor: z.object({
            orderEventId: orderEventIdZodSchema.or(
                z.array(orderEventIdZodSchema),
            ),
        }),
    }),
);

type OrderPayLaterOnPremiseCancelledEvent = z.infer<
    typeof orderPayLaterOnPremiseCancelledZodObject
>;
const orderPayLaterOnPremiseCancelledZodSchema =
    orderPayLaterOnPremiseCancelledZodObject as z.ZodSchema<OrderPayLaterOnPremiseCancelledEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPayLaterOnPremiseCancelledEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;
type Params = Except<FactoryParams, "signature" | "cancels"> & {
    cancels: Required<OrderPayLaterOnPremiseCancelledEvent["cancels"]>;
};

function orderPayLaterOnPremiseCancelledEventFactory(
    params: Params,
): OrderPayLaterOnPremiseCancelledEvent {
    return {
        ..._orderEventBaseFactory({
            ...params,
            signature: signatureFactory(
                params.orderId,
                "ON_PREMISE_PAY_LATER_CANCELLED",
                asArray(params.paymentFor.orderEventId),
            ),
        }),
        paymentFor: params.paymentFor,
        type: "ON_PREMISE_PAY_LATER_CANCELLED",
        paymentProvider: params.paymentProvider,
        price: params.price,
    };
}

const isOrderPayLaterOnPremiseCancelledEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPayLaterOnPremiseCancelledEvent => {
    return (
        event._type === "ORDER_EVENT" &&
        event?.type === "ON_PREMISE_PAY_LATER_CANCELLED"
    );
};

export {
    isOrderPayLaterOnPremiseCancelledEvent,
    orderPayLaterOnPremiseCancelledEventFactory,
    orderPayLaterOnPremiseCancelledZodObject,
    orderPayLaterOnPremiseCancelledZodSchema,
};
export type { OrderPayLaterOnPremiseCancelledEvent };
